import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { ConfirmType } from "@/components/ConfirmType";
import { InlineEdit } from "@/components/InlineEdit";
import { Modal } from "@/components/Modal";
import { ProviderGlyph } from "@/components/ProviderGlyph";
import { useToast } from "@/components/Toast";
import type { ProxyKey, RealKey } from "@/types";

type HealthResult = { status: string; detail: string };

export function RealKeyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [real, setReal] = useState<RealKey | null>(null);
  const [proxies, setProxies] = useState<ProxyKey[]>([]);
  const [rotateOpen, setRotateOpen] = useState(false);
  const [askDelete, setAskDelete] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [health, setHealth] = useState<HealthResult | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const toast = useToast();

  async function load() {
    try {
      const r = await api<RealKey>(`/api/admin/real-keys/${id}`);
      setReal(r);
      const p = await api<ProxyKey[]>(`/api/admin/proxy-keys?real_key_id=${id}`);
      setProxies(p);
    } catch (e: any) {
      setErr(e?.message ?? "load failed");
    }
  }
  useEffect(() => {
    load();
  }, [id]);

  async function runHealthCheck() {
    setHealth(null);
    setHealthLoading(true);
    try {
      const r = await api<HealthResult>(
        `/api/admin/real-keys/${id}/health-check`,
        { method: "POST" }
      );
      setHealth(r);
    } catch (e: any) {
      setHealth({ status: "error", detail: e?.message ?? "request failed" });
    } finally {
      setHealthLoading(false);
    }
  }

  async function doDelete() {
    try {
      const r = await api<{ status: string; approval_id?: number; warning?: string }>(
        `/api/admin/real-keys/${id}`,
        { method: "DELETE" }
      );
      if (r.status === "pending_approval") {
        toast.info(
          `Deletion queued (approval #${r.approval_id}) — needs a 2nd super-admin to approve`
        );
        nav("/approvals");
      } else {
        if (r.warning) toast.error(r.warning);
        else toast.success("Real key deleted");
        nav("/real-keys");
      }
    } catch (e: any) {
      setErr(e?.message ?? "delete failed");
      toast.error("Delete failed");
    } finally {
      setAskDelete(false);
    }
  }

  if (!real) return <div className="p-4 text-slate-500">{err ?? "Loading…"}</div>;

  const activeProxies = proxies.filter((p) => !p.revoked_at);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/real-keys" className="hover:text-slate-200">
          Real keys
        </Link>
        <span>/</span>
        <span className="text-slate-300">{real.label}</span>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <ProviderGlyph provider={real.provider} size={40} />
          <div>
            <h1 className="text-3xl display">
              <InlineEdit
                value={real.label}
                onSave={async (next) => {
                  await api(`/api/admin/real-keys/${id}`, {
                    method: "PATCH",
                    json: { label: next },
                  });
                  await load();
                }}
                placeholder="(click to set a label)"
              />
            </h1>
            <p className="text-sm text-slate-400 mono">
              {real.provider} · {real.upstream_base_url}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            className="btn-secondary"
            onClick={runHealthCheck}
            disabled={healthLoading}
            title="Probe the upstream with this key to confirm it's still valid"
          >
            {healthLoading ? "Testing…" : "Test upstream"}
          </button>
          <button className="btn-secondary" onClick={() => setRotateOpen(true)}>
            Rotate secret
          </button>
          <button className="btn-danger" onClick={() => setAskDelete(true)}>
            Delete
          </button>
        </div>
      </div>

      {err && <div className="card text-rose-300 text-sm">{err}</div>}

      {real.quota_exhausted && (
        <div className="card border border-rose-500/40 bg-rose-500/10 text-rose-200 flex items-start gap-3">
          <span className="text-2xl leading-none">⚠️</span>
          <div className="flex-1">
            <div className="font-semibold">Credit exhausted at upstream</div>
            <div className="text-xs opacity-90 mt-1">
              The credit-low scanner detected ≥3 <code>402 / "credit balance"</code> errors in the
              last 60 seconds for this real key. Top up the upstream provider — the next
              successful 2xx call will auto-clear this banner.
              {real.issuer_email && (
                <>
                  {" "}Account on file: <span className="mono">{real.issuer_email}</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {health && <HealthBanner result={health} />}

      <QuotaCard real={real} onChanged={load} />

      <div className="grid grid-cols-2 gap-6">
        <div className="card space-y-2">
          <h2 className="font-semibold mb-2">Details</h2>
          <Row label="Auth scheme" value={real.auth_scheme} />
          <Row label="Auth header/name" value={real.auth_header_name ?? "—"} />
          <Row label="Basic username" value={real.auth_username ?? "—"} />
          <Row label="Last rotated" value={new Date(real.rotated_at).toLocaleString()} />
          <Row label="Created" value={new Date(real.created_at).toLocaleString()} />
          <div className="flex items-start justify-between text-sm pt-1">
            <span className="text-slate-500 pt-2">Notes</span>
            <div className="text-slate-200 text-right max-w-[60%]">
              <InlineEdit
                multiline
                value={real.notes ?? ""}
                placeholder="(click to add notes — never stored in the audit log)"
                onSave={async (next) => {
                  await api(`/api/admin/real-keys/${id}`, {
                    method: "PATCH",
                    json: { notes: next || null },
                  });
                  await load();
                }}
              />
            </div>
          </div>
          <div className="flex items-start justify-between text-sm">
            <span className="text-slate-500">Rotation webhook</span>
            <div className="text-slate-200 text-right max-w-[60%] mono text-xs break-all">
              <InlineEdit
                value={real.rotation_webhook_url ?? ""}
                placeholder="(click to add — POSTed on every rotation)"
                onSave={async (next) => {
                  await api(`/api/admin/real-keys/${id}`, {
                    method: "PATCH",
                    json: { rotation_webhook_url: next || null },
                  });
                  await load();
                }}
              />
            </div>
          </div>
        </div>
        <div className="card">
          <h2 className="font-semibold mb-3">Bound proxy keys</h2>
          {activeProxies.length === 0 ? (
            <p className="text-sm text-slate-500">None yet.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {activeProxies.map((p) => (
                <li key={p.id} className="flex items-center justify-between">
                  <Link to={`/proxy-keys/${p.id}`} className="hover:text-accent-500">
                    {p.label}{" "}
                    <span className="text-slate-500 mono text-xs">({p.prefix}…)</span>
                  </Link>
                  <span className="text-xs text-slate-500">{p.project ?? "no project"}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <Modal open={rotateOpen} title={`Rotate ${real.label}`} onClose={() => setRotateOpen(false)}>
        <RotateForm
          realKeyId={real.id}
          onDone={async () => {
            setRotateOpen(false);
            await load();
          }}
        />
      </Modal>

      <ConfirmType
        open={askDelete}
        title={`Delete ${real.label}?`}
        explanation={`This permanently removes the real key. All bound proxy keys must already be revoked. This cannot be undone — losing the underlying plaintext means losing access to the upstream service.`}
        confirmPhrase={real.label}
        dangerLabel="Delete real key"
        onCancel={() => setAskDelete(false)}
        onConfirm={doDelete}
      />
    </div>
  );
}

function HealthBanner({ result }: { result: HealthResult }) {
  const tone =
    result.status === "alive"
      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"
      : result.status === "unauthenticated" || result.status === "upstream_error"
      ? "bg-rose-500/10 border-rose-500/30 text-rose-200"
      : "bg-amber-500/10 border-amber-500/30 text-amber-200";
  return (
    <div className={`card border ${tone}`}>
      <div className="text-sm font-semibold capitalize">{result.status.replace("_", " ")}</div>
      <div className="text-xs opacity-80 mt-1">{result.detail}</div>
    </div>
  );
}


function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-200 text-right max-w-[60%] break-words">{value}</span>
    </div>
  );
}

function QuotaCard({ real, onChanged }: { real: RealKey; onChanged: () => Promise<void> }) {
  const amount = real.quota_amount ? Number(real.quota_amount) : null;
  const used = Number(real.quota_used ?? 0);
  const unit = real.quota_unit ?? "";
  const hasQuota = amount !== null && amount > 0;
  const remaining = hasQuota ? Math.max(0, amount! - used) : null;
  const pctUsed = hasQuota ? Math.min(100, Math.round((used / amount!) * 100)) : 0;
  const pctRemaining = 100 - pctUsed;
  const tone =
    !hasQuota
      ? "bg-slate-500"
      : pctRemaining <= 10
      ? "bg-rose-500"
      : pctRemaining <= 30
      ? "bg-amber-500"
      : "bg-emerald-500";

  async function patch(field: string, raw: string) {
    const next = raw.trim();
    const body: any = {};
    body[field] = next === "" ? null : next;
    await api(`/api/admin/real-keys/${real.id}`, { method: "PATCH", json: body });
    await onChanged();
  }

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">Provenance &amp; Quota</h2>
        {hasQuota && (
          <span className="text-xs text-slate-400">
            {used.toLocaleString()} / {amount!.toLocaleString()} {unit}
            {real.quota_period ? ` · ${real.quota_period}` : ""}
            {remaining !== null && (
              <span
                className={
                  pctRemaining <= 10
                    ? "ml-2 text-rose-300"
                    : pctRemaining <= 30
                    ? "ml-2 text-amber-300"
                    : "ml-2 text-emerald-300"
                }
              >
                ({remaining.toLocaleString()} left)
              </span>
            )}
          </span>
        )}
      </div>

      {hasQuota && (
        <div className="h-2 w-full rounded-full bg-white/[0.04] overflow-hidden">
          <div
            className={`h-full ${tone} transition-all`}
            style={{ width: `${pctUsed}%` }}
            aria-label={`${pctUsed}% used`}
          />
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm pt-1">
        <div className="flex items-start justify-between">
          <span className="text-slate-500">Issuer email</span>
          <div className="text-slate-200 text-right max-w-[60%] mono text-xs break-all">
            <InlineEdit
              value={real.issuer_email ?? ""}
              placeholder="(click to record which account issued this key)"
              onSave={(next) => patch("issuer_email", next)}
            />
          </div>
        </div>
        <div className="flex items-start justify-between">
          <span className="text-slate-500">Quota period</span>
          <div className="text-slate-200 text-right max-w-[60%]">
            <InlineEdit
              value={real.quota_period ?? ""}
              placeholder="(daily / monthly / lifetime)"
              onSave={(next) => patch("quota_period", next)}
            />
          </div>
        </div>
        <div className="flex items-start justify-between">
          <span className="text-slate-500">Quota amount</span>
          <div className="text-slate-200 text-right max-w-[60%] mono">
            <InlineEdit
              value={real.quota_amount ?? ""}
              placeholder="(click to set total)"
              onSave={(next) => patch("quota_amount", next)}
            />
          </div>
        </div>
        <div className="flex items-start justify-between">
          <span className="text-slate-500">Used so far</span>
          <div className="text-slate-200 text-right max-w-[60%] mono">
            <InlineEdit
              value={real.quota_used ?? "0"}
              placeholder="0"
              onSave={(next) => patch("quota_used", next)}
            />
          </div>
        </div>
        <div className="flex items-start justify-between">
          <span className="text-slate-500">Unit</span>
          <div className="text-slate-200 text-right max-w-[60%] mono">
            <InlineEdit
              value={real.quota_unit ?? ""}
              placeholder="USD / tokens / requests"
              onSave={(next) => patch("quota_unit", next)}
            />
          </div>
        </div>
        <div className="flex items-start justify-between">
          <span className="text-slate-500" title="When set, the gateway rejects requests with 429 once monthly spend exceeds this cap">
            Monthly budget cap (USD)
          </span>
          <div className="text-slate-200 text-right max-w-[60%] mono">
            <InlineEdit
              value={real.monthly_budget_usd ?? ""}
              placeholder="(no cap — set USD/month to auto-throttle)"
              onSave={(next) => patch("monthly_budget_usd", next)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function maskFingerprint(v: string): string {
  if (!v) return "—";
  const trimmed = v.trim();
  if (trimmed.length <= 8) return "••••••";
  return "••••" + trimmed.slice(-4);
}

function RotateForm({ realKeyId, onDone }: { realKeyId: number; onDone: () => void }) {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await api(`/api/admin/real-keys/${realKeyId}/rotate`, {
        method: "POST",
        json: { new_value: value },
      });
      toast.success("Key rotated — all bound proxies using new value");
      onDone();
    } catch (e: any) {
      setErr(e?.message ?? "rotate failed");
      toast.error("Rotate failed");
    } finally {
      setLoading(false);
    }
  }
  const fingerprint = maskFingerprint(value);
  const showPreview = value.trim().length > 0;
  return (
    <form onSubmit={submit} className="space-y-4">
      <p className="text-sm text-slate-400">
        Paste the NEW upstream secret. Every proxy key bound to this real key will instantly start
        using it — no consumer redeploys needed.
      </p>
      <input
        className="input mono"
        type="password"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoComplete="new-password"
        placeholder="Paste new key here"
        required
      />
      {showPreview && (
        <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-slate-500">New key fingerprint</span>
            <span className="mono text-slate-200">{fingerprint}</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5">
            The last 4 characters of your pasted value — use them to confirm you pasted the
            right key from your password manager before confirming.
          </p>
        </div>
      )}
      {err && <div className="text-rose-300 text-xs">{err}</div>}
      <div className="flex justify-end">
        <button className="btn-primary" disabled={loading || !showPreview} type="submit">
          {loading ? "Rotating…" : "Rotate now"}
        </button>
      </div>
    </form>
  );
}
